import {
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import type { ColumnSizingState, SortingState } from "@tanstack/react-table";
import * as React from "react";
import { useTrackedBrowserStorage } from "./use-tracked-browser-storage";

/**
 * Hook for managing persisted column state (sizing, ordering, pinning, sorting).
 * Persists state to sessionStorage for tab-specific behavior.
 *
 * @param tableName - Unique table name used for sessionStorage keys
 * @param options - Optional configuration
 * @param options.getDefaultPinnedColumns - Function that returns column IDs to pin by default
 * @returns Methods and state for managing persisted column state
 */
export function usePersistedColumnState(
  tableName: string,
  options?: { getDefaultPinnedColumns?: () => string[] },
) {
  // Use sessionStorage for tab-specific column state
  const [columnSizing, setColumnSizing] =
    useTrackedBrowserStorage<ColumnSizingState>(
      `${tableName}::sizes`,
      {},
      {
        storage: "sessionStorage",
      },
    );

  const [columnOrder, setColumnOrder] = useTrackedBrowserStorage<string[]>(
    `${tableName}::order`,
    [],
    {
      storage: "sessionStorage",
    },
  );

  const [pinnedColumns, setPinnedColumns] = useTrackedBrowserStorage<string[]>(
    `${tableName}::pinned`,
    options?.getDefaultPinnedColumns?.() || [],
    {
      storage: "sessionStorage",
    },
  );

  const [originalPositions, setOriginalPositions] = useTrackedBrowserStorage<
    Record<string, number>
  >(
    `${tableName}::pinned-original-positions`,
    {},
    {
      storage: "sessionStorage",
    },
  );

  const [sorting, setSorting] = useTrackedBrowserStorage<SortingState>(
    `${tableName}::sorting`,
    [],
    {
      storage: "sessionStorage",
    },
  );

  const [wrappedColumns, setWrappedColumns] = useTrackedBrowserStorage<
    string[]
  >(`${tableName}::wrapped`, [], {
    storage: "sessionStorage",
  });

  // Toggle a column's pinned state
  const toggleColumnPin = React.useCallback(
    (columnId: string) => {
      setPinnedColumns((current) => {
        const isPinned = current.includes(columnId);

        if (isPinned) {
          // Unpin: restore to original position
          const originalPos = originalPositions[columnId];
          const defaultPinned = options?.getDefaultPinnedColumns?.() || [];
          const isDefaultPinned = defaultPinned.includes(columnId);

          setColumnOrder((currentOrder) => {
            const withoutColumn = currentOrder.filter((id) => id !== columnId);

            // Determine the target position for the unpinned column
            let targetPosition: number;

            if (originalPos !== undefined && originalPos >= 0) {
              // User manually pinned this column - restore to stored position
              const remainingPinnedColumns = withoutColumn.filter(
                (id) => current.includes(id) && id !== columnId,
              );
              const minValidPosition = remainingPinnedColumns.length;
              const maxValidPosition = withoutColumn.length;

              targetPosition = Math.min(
                Math.max(originalPos, minValidPosition),
                maxValidPosition,
              );
            } else if (isDefaultPinned) {
              // This was pinned by default - restore to its default position
              // Position should be after ALL remaining pinned columns (default + manually pinned)
              const remainingPinnedColumns = withoutColumn.filter(
                (id) => current.includes(id) && id !== columnId,
              );
              targetPosition = remainingPinnedColumns.length;
            } else {
              // No stored position and not default pinned - add to end
              targetPosition = withoutColumn.length;
            }

            // Insert at the calculated position
            const newOrder = [...withoutColumn];
            newOrder.splice(targetPosition, 0, columnId);
            return newOrder;
          });

          // Remove from original positions tracking
          setOriginalPositions((positions) => {
            const newPositions = { ...positions };
            delete newPositions[columnId];
            return newPositions;
          });

          // Remove from pinned columns
          return current.filter((id) => id !== columnId);
        } else {
          // Pin: remember current position and move to the left
          const currentPosition = columnOrder.indexOf(columnId);

          // Store original position only if it's valid
          if (currentPosition >= 0) {
            setOriginalPositions((positions) => ({
              ...positions,
              [columnId]: currentPosition,
            }));
          }

          // Add to pinned columns
          const newPinned = [...current, columnId];

          // Update column order to move pinned column to the left
          setColumnOrder((currentOrder) => {
            const withoutColumn = currentOrder.filter((id) => id !== columnId);
            // Place it after other existing pinned columns
            const existingPinnedInOrder = withoutColumn.filter(
              (id) => current.includes(id), // Use current pinned state, not newPinned
            );
            const unpinnedInOrder = withoutColumn.filter(
              (id) => !current.includes(id), // Use current pinned state
            );
            return [...existingPinnedInOrder, columnId, ...unpinnedInOrder];
          });

          return newPinned;
        }
      });
    },
    [
      setPinnedColumns,
      setColumnOrder,
      columnOrder,
      originalPositions,
      setOriginalPositions,
      options?.getDefaultPinnedColumns,
    ],
  );

  // Handle drag end to reorder columns
  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);

      // Enhanced validation for pinned column constraints
      const isPinnedActive = pinnedColumns.includes(active.id as string);
      const isPinnedOver = pinnedColumns.includes(over.id as string);

      // Don't allow dragging unpinned columns before pinned columns
      if (!isPinnedActive && isPinnedOver && newIndex < oldIndex) {
        return;
      }

      // Ensure pinned columns stay grouped together at the beginning
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);

      // Validate that pinned columns remain at the start
      const pinnedInNewOrder = newOrder.filter((id) =>
        pinnedColumns.includes(id),
      );
      const unpinnedInNewOrder = newOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );

      // Check if any unpinned column appears before any pinned column
      const firstPinnedIndex = newOrder.findIndex((id) =>
        pinnedColumns.includes(id),
      );

      let validatedOrder = newOrder;
      if (firstPinnedIndex > 0 && pinnedInNewOrder.length > 0) {
        // Invalid state: pinned columns are not at the start
        // Reorder to maintain pinned columns at the beginning
        validatedOrder = [...pinnedInNewOrder, ...unpinnedInNewOrder];
      }

      setColumnOrder(validatedOrder);

      // Clean up original positions for moved columns that are no longer pinned
      setOriginalPositions((positions) => {
        const updatedPositions = { ...positions };
        let hasChanges = false;

        validatedOrder.forEach((colId) => {
          if (
            !pinnedColumns.includes(colId) &&
            updatedPositions[colId] !== undefined
          ) {
            // This column was moved while pinned, remove its original position tracking
            delete updatedPositions[colId];
            hasChanges = true;
          }
        });

        return hasChanges ? updatedPositions : positions;
      });
    },
    [columnOrder, setColumnOrder, pinnedColumns, setOriginalPositions],
  );

  // Unpin all columns and restore them to their original positions
  const unpinAllColumns = React.useCallback(() => {
    setColumnOrder((currentOrder) => {
      // Remove pinned columns from current order first
      const orderWithoutPinned = currentOrder.filter(
        (id) => !pinnedColumns.includes(id),
      );

      // Re-insert each pinned column back at its recorded original position
      // If original position is unavailable, append to the end.
      const reintegratedOrder = [...orderWithoutPinned];
      Object.entries(originalPositions).forEach(([colId, pos]) => {
        const index = Math.min(
          Math.max(0, pos as number),
          reintegratedOrder.length,
        );
        reintegratedOrder.splice(index, 0, colId);
      });

      return reintegratedOrder;
    });

    // Clear pin state and original position tracking
    setPinnedColumns([]);
    setOriginalPositions({});
  }, [
    pinnedColumns,
    originalPositions,
    setColumnOrder,
    setPinnedColumns,
    setOriginalPositions,
  ]);

  // Reset pinned columns to default
  const resetToDefaultPinned = React.useCallback(() => {
    // Recalculate defaults based on current column state
    const defaultPinned = options?.getDefaultPinnedColumns?.() || [];

    // If no defaults, just unpin all
    if (defaultPinned.length === 0) {
      unpinAllColumns();
      return;
    }

    // Set to default pinned columns
    setPinnedColumns(defaultPinned);

    // Clear original positions since we're resetting
    setOriginalPositions({});

    // Update column order to put default pinned columns first
    setColumnOrder((currentOrder) => {
      const unpinnedColumns = currentOrder.filter(
        (id) => !defaultPinned.includes(id),
      );
      const pinnedInOrder = currentOrder.filter((id) =>
        defaultPinned.includes(id),
      );
      return [...pinnedInOrder, ...unpinnedColumns];
    });
  }, [
    options?.getDefaultPinnedColumns,
    unpinAllColumns,
    setPinnedColumns,
    setOriginalPositions,
    setColumnOrder,
  ]);

  /**
   * Initialize column order from column definitions, putting pinned columns first.
   * Returns a function to be called from a useEffect in the component.
   *
   * Note: This is for initial setup and column definition changes only.
   * Ongoing pinned state changes are handled by toggleColumnPin and resetToDefaultPinned.
   * We capture pinnedColumns at call time (not in dependencies) to avoid unnecessary recreations.
   *
   * @param columnDefs - Array of column definitions
   * @returns Callback to initialize column order
   */
  const getInitializeColumnOrder = React.useCallback(
    (columnDefs: Array<{ id?: string }>) => {
      // Capture current pinned state at call time (not from closure)
      // This ensures we use the pinned state when this function is called, not when it was created
      const getPinnedAtCallTime = () => pinnedColumns;

      return () => {
        setColumnOrder((prevOrder) => {
          const currentIds = columnDefs.map((col) => col.id!).filter(Boolean);

          if (prevOrder.length === 0) {
            // No saved order, use default with pinned columns first
            const currentPinned = getPinnedAtCallTime();
            const pinnedFirst = currentIds.filter((id) =>
              currentPinned.includes(id),
            );
            const unpinned = currentIds.filter(
              (id) => !currentPinned.includes(id),
            );
            return [...pinnedFirst, ...unpinned];
          }

          // Filter out any saved columns that no longer exist and add any new columns at the end
          const validSavedColumns = prevOrder.filter((id) =>
            currentIds.includes(id),
          );
          const newColumns = currentIds.filter((id) => !prevOrder.includes(id));
          const newOrder = [...validSavedColumns, ...newColumns];

          // Only update if order actually changed
          if (
            newOrder.length !== prevOrder.length ||
            newOrder.some((id, i) => prevOrder[i] !== id)
          ) {
            return newOrder;
          }

          return prevOrder;
        });
      };
    },
    [setColumnOrder, pinnedColumns],
  );

  // Toggle a column's wrapped state
  const toggleColumnWrap = React.useCallback(
    (columnId: string) => {
      setWrappedColumns((current) => {
        const isWrapped = current.includes(columnId);

        if (isWrapped) {
          // Unwrap: remove from wrapped columns
          return current.filter((id) => id !== columnId);
        } else {
          // Wrap: add to wrapped columns
          return [...current, columnId];
        }
      });
    },
    [setWrappedColumns],
  );

  // Configure sensors so that a small pointer movement is required before a drag starts.
  // This lets plain clicks (no movement) propagate so we can still sort or open filters
  // while keeping the drag-to-reorder behaviour intact.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // Number of pixels the pointer has to move before a drag is initiated
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return {
    columnOrder,
    setColumnOrder, // Persistence is handled by the useTrackedBrowserStorage hook
    columnSizing,
    setColumnSizing, // Persistence is handled by the useTrackedBrowserStorage hook
    pinnedColumns,
    setPinnedColumns,
    resetToDefaultPinned,
    toggleColumnPin,
    wrappedColumns,
    setWrappedColumns,
    toggleColumnWrap,
    sorting,
    setSorting, // Persistence is handled by the useTrackedBrowserStorage hook
    dndState: {
      sensors,
      handleDragEnd,
    },
    getInitializeColumnOrder, // Helper for column order initialization
  };
}
